<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Icon, IconButton } from '@Shelly/shelly-design-system';

	let className = '';
	export { className as class };

	export let tone: 'camera' | 'premium' = 'camera';
	export let icon: string;
	export let actionLayout: 'full' | 'auto' = 'full';
	/**
	 * Named `action` slots stay compiled-in even when the parent wraps
	 * them in `{#if}`. Pass `false` so the action row is not reserved.
	 */
	export let showAction: boolean | undefined;
	/** Top-right close control. Parent owns persist / hide. */
	export let dismissable = false;

	const dispatch = createEventDispatcher<{ dismiss: void }>();
</script>

<section
	class={`feature-promo-banner feature-promo-banner--${tone} feature-promo-banner--action-${actionLayout} ${dismissable ? 'feature-promo-banner--dismissable' : ''} ${className}`}
>
	{#if dismissable}
		<div role="presentation" class="feature-promo-banner__dismiss" on:click|stopPropagation>
			<IconButton
				variant="secondary"
				shape="circle"
				size="s"
				icon="fa-solid fa-times"
				aria-label={$format('button_close', { default: 'Close' })}
				on:click={() => dispatch('dismiss')}
			/>
		</div>
	{/if}
	<div class="feature-promo-banner__content">
		<div class="feature-promo-banner__icon" aria-hidden="true">
			<Icon {icon} size="m" />
		</div>
		<div class="feature-promo-banner__copy">
			<slot />
		</div>
	</div>

	{#if showAction ?? $$slots.action}
		<div class="feature-promo-banner__action">
			<slot name="action" />
		</div>
	{/if}
</section>

<style>
	.feature-promo-banner {
		--promo-padding: 16px;
		--promo-dismiss-size: 32px;
		--promo-content-indent: 40px;
		--promo-accent: #db5cf4;
		--promo-accent-start: #db5cf4;
		--promo-accent-end: #8e36ec;
		--promo-accent-soft: rgba(219, 92, 244, 0.34);
		--promo-accent-border: rgba(219, 92, 244, 0.55);
		--promo-accent-glow: rgba(219, 92, 244, 0.9);
		--promo-surface: var(--color-1, #242527);
		--promo-button-background: linear-gradient(
			90deg,
			var(--promo-accent-start) 0%,
			var(--promo-accent-end) 100%
		);
		--promo-button-color: #ffffff;
		position: relative;
		overflow: hidden;
		box-sizing: border-box;
		width: 100%;
		max-width: 100%;
		padding: var(--promo-padding);
		border: 1px solid var(--promo-accent-border);
		border-radius: var(--radius-big);
		background:
			radial-gradient(120% 120% at 50% 0%, var(--promo-accent-soft) 0%, transparent 62%),
			var(--promo-surface);
		box-shadow: 0 30px 70px -46px var(--promo-accent-glow);
		color: #ffffff;
	}

	.feature-promo-banner__dismiss {
		position: absolute;
		top: var(--promo-padding);
		right: var(--promo-padding);
		z-index: 1;
	}

	/*
	 * Keep banner padding even on both sides. Only the copy row insets
	 * so the title does not run under the close control; the CTA stays
	 * full-width and aligned with the left edge of the icon.
	 */
	.feature-promo-banner--dismissable .feature-promo-banner__content {
		padding-right: calc(var(--promo-dismiss-size) + 8px);
	}

	.feature-promo-banner__dismiss :global(button) {
		background: rgba(255, 255, 255, 0.16) !important;
		color: #ffffff !important;
		box-shadow: none !important;
	}

	.feature-promo-banner--premium {
		--promo-accent: var(--color-premium);
		--promo-accent-start: var(--color-premium);
		--promo-accent-end: var(--color-premium);
		--promo-accent-soft: color-mix(in srgb, var(--color-premium) 28%, transparent);
		--promo-accent-border: color-mix(in srgb, var(--color-premium) 58%, transparent);
		--promo-accent-glow: color-mix(in srgb, var(--color-premium) 88%, transparent);
		--promo-surface: rgba(255, 255, 255, 0.055);
		--promo-button-background: var(--gradient-premium);
		--promo-button-color: #171717;
	}

	.feature-promo-banner__content {
		display: grid;
		grid-template-columns: 28px minmax(0, 1fr);
		align-items: start;
		gap: 12px;
	}

	.feature-promo-banner__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		color: var(--promo-accent);
	}

	.feature-promo-banner--premium .feature-promo-banner__icon :global(i) {
		background: var(--gradient-premium);
		background-clip: text;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
	}

	.feature-promo-banner__copy {
		min-width: 0;
	}

	.feature-promo-banner__copy :global(.feature-promo-banner__title),
	.feature-promo-banner__copy :global(.feature-promo-banner__title *) {
		color: #ffffff !important;
		font-weight: 700;
	}

	.feature-promo-banner__copy :global(.feature-promo-banner__text),
	.feature-promo-banner__copy :global(.feature-promo-banner__text *) {
		color: #ffffff !important;
		font-weight: 500;
		line-height: 1.55;
	}

	.feature-promo-banner__action {
		display: flex;
		width: 100%;
		margin-top: 14px;
	}

	.feature-promo-banner--action-auto .feature-promo-banner__action {
		justify-content: flex-start;
		width: calc(100% - var(--promo-content-indent));
		margin-left: var(--promo-content-indent);
	}

	.feature-promo-banner__action :global(.feature-promo-banner__button) {
		display: flex !important;
		box-sizing: border-box;
		width: 100% !important;
		max-width: none !important;
		min-height: 46px;
		overflow: hidden;
		border: 0 !important;
		border-radius: 10px !important;
		background: var(--promo-button-background) !important;
		background-image: var(--promo-button-background) !important;
		background-position: 0 50% !important;
		background-size: 160% 100% !important;
		box-shadow: 0 12px 30px -14px var(--promo-accent-glow) !important;
		color: var(--promo-button-color) !important;
		transition:
			background-position 400ms ease,
			box-shadow 240ms ease,
			filter 240ms ease,
			transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1);
	}

	.feature-promo-banner--action-auto
		.feature-promo-banner__action
		:global(.feature-promo-banner__button) {
		flex: 0 0 auto;
		width: auto !important;
		max-width: max-content !important;
	}

	.feature-promo-banner__action :global(.feature-promo-banner__button *) {
		color: var(--promo-button-color) !important;
	}

	.feature-promo-banner__action :global(.feature-promo-banner__button:not(:disabled):hover) {
		background-position: 100% 50% !important;
		box-shadow: 0 18px 38px -13px var(--promo-accent-glow) !important;
		filter: brightness(1.04);
		transform: translateY(-1px);
	}

	.feature-promo-banner__action :global(.feature-promo-banner__button:not(:disabled):active) {
		box-shadow: 0 8px 22px -16px var(--promo-accent-glow) !important;
		filter: brightness(0.98);
		transform: translateY(0) scale(0.985);
	}

	.feature-promo-banner__action :global(.feature-promo-banner__button:focus-visible) {
		outline: 2px solid color-mix(in srgb, var(--promo-accent) 82%, #ffffff) !important;
		outline-offset: 3px;
	}

	@media screen and (max-width: 420px) {
		.feature-promo-banner {
			--promo-padding: 14px;
			--promo-content-indent: 38px;
		}

		.feature-promo-banner__content {
			gap: 10px;
		}

		.feature-promo-banner__action {
			margin-top: 12px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.feature-promo-banner__action :global(.feature-promo-banner__button) {
			transition: none;
		}
	}
</style>
